import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useCoupons } from '../hooks/use-coupons';
import { useClippedCoupons } from '../hooks/use-clipped-coupons';
import { useCouponAuth } from '../hooks/use-coupon-auth';
import { CouponCard } from '../components/CouponCard';
import { CouponDetailSheet } from '../components/CouponDetailSheet';
import { ClipToastProvider, useClipToast } from '../context/ClipToastContext';
import { Coupon } from '../types/coupons';
import { SEO } from '@/components/SEO';
import { IconAlertCircle, IconCircleCheck, IconX } from '@tabler/icons-react';

// Toast component for clip notifications
const ClipToastNotification = () => {
  const { toastState, hideToast } = useClipToast();

  if (!toastState.show) return null;

  const isSuccess = toastState.type === 'success';

  return (
    <div className="fixed top-4 right-4 z-50 w-full max-w-sm animate-in slide-in-from-top-2 fade-in duration-300">
      <div className="bg-white rounded-lg shadow-lg border border-border overflow-hidden">
        <div className="p-4">
          <div className="flex items-start gap-3">
            <div className={`p-2 rounded-lg flex-shrink-0 w-9 h-9 ${isSuccess ? 'bg-emerald-500/10' : 'bg-red-500/10'}`}>
              {isSuccess ? (
                <IconCircleCheck className="h-5 w-5 text-emerald-500" />
              ) : (
                <IconAlertCircle className="h-5 w-5 text-red-500" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-foreground">
                {isSuccess ? 'Coupon Saved!' : 'Unable to Save'}
              </h4>
              <p className="text-sm text-muted-foreground mt-1">
                {toastState.message}
              </p>
            </div>
            <button
              onClick={hideToast}
              className="p-1 rounded-md hover:bg-muted flex-shrink-0"
            >
              <IconX className="h-4 w-4 text-muted-foreground" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const CouponsContent = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'all' | 'clipped'>('all');
  const [selectedCoupon, setSelectedCoupon] = useState<Coupon | null>(null);
  const [detailSheetOpen, setDetailSheetOpen] = useState(false);

  const { isAuthenticated } = useCouponAuth();
  const { data: coupons, isLoading, error } = useCoupons();
  const { data: clippedCoupons = [] } = useClippedCoupons();

  // Extract unique coupon types from loaded coupons, with Weekly Specials first then alphabetical
  const couponTypes = coupons
    ? [...new Set(coupons.map((c) => c.category).filter(Boolean))].sort((a, b) => {
        if (a === 'Weekly Specials') return -1;
        if (b === 'Weekly Specials') return 1;
        return (a as string).localeCompare(b as string);
      })
    : [];

  // Filter coupons by selected type
  const filteredCoupons = selectedType
    ? (coupons || []).filter((c) => c.category === selectedType)
    : (coupons || []);

  const displayCoupons = activeTab === 'clipped' ? clippedCoupons : filteredCoupons;

  // Handle URL param to set active tab (e.g., ?tab=clipped for Saved Offers)
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'clipped' && isAuthenticated) {
      setActiveTab('clipped');
      // Clear the URL param after setting tab
      searchParams.delete('tab');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, isAuthenticated]);

  // Handle URL param to open coupon detail sheet
  useEffect(() => {
    const openParam = searchParams.get('open');
    if (openParam && coupons) {
      const coupon = coupons.find(c => (c.id || c.offerId) === openParam);
      if (coupon) {
        setSelectedCoupon(coupon);
        setDetailSheetOpen(true);
        // Clear the URL param after opening
        setSearchParams({}, { replace: true });
      }
    }
  }, [searchParams, coupons, setSearchParams]);

  return (
    <div className="min-h-screen bg-neutral-50 py-8 md:py-12">
      <SEO
        title="Digital Coupons and Offers | Clip and Save"
        description="Save money with digital coupons. Clip coupons online and redeem them at checkout for instant savings."
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-3xl sm:text-4xl font-bold text-foreground">
              Digital Coupons
            </h1>
          </div>
          <p className="text-muted-foreground text-lg">
            Clip coupons and save on your next shopping trip
          </p>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-6">
            <IconAlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>
              Failed to load coupons. Please try again later.
            </AlertDescription>
          </Alert>
        )}

        <Tabs
          value={activeTab}
          onValueChange={(v) => setActiveTab(v as 'all' | 'clipped')}
          className="mb-6"
        >
          <div className="flex flex-col sm:flex-row items-center sm:items-center justify-between gap-4 mb-6">
            <TabsList>
              <TabsTrigger value="all">All Offers</TabsTrigger>
              <TabsTrigger value="clipped" disabled={!isAuthenticated}>
                Saved Offers
              </TabsTrigger>
            </TabsList>

            {activeTab === 'all' && couponTypes.length > 0 && (
              <div className="flex flex-wrap justify-center sm:justify-end gap-2">
                <Select
                  value={selectedType || 'all'}
                  onValueChange={(value) =>
                    setSelectedType(value === 'all' ? null : value)
                  }
                >
                  <SelectTrigger className="w-[160px] sm:w-[180px]">
                    <SelectValue placeholder="Coupon Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    {couponTypes.map((type) => (
                      <SelectItem key={type} value={type as string}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <TabsContent value="all" className="mt-0">
            {isLoading && (
              <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="border rounded-lg overflow-hidden">
                    <Skeleton className="w-full aspect-[16/9]" />
                    <div className="p-5 space-y-3">
                      <Skeleton className="h-5 w-3/4" />
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-2/3" />
                      <Skeleton className="h-9 w-full mt-4" />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {!isLoading && !error && displayCoupons.length > 0 && (
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 md:gap-6">
                {displayCoupons.map((coupon) => (
                  <CouponCard
                    key={coupon.id || coupon.offerId}
                    coupon={coupon}
                  />
                ))}
              </div>
            )}

            {!isLoading && !error && displayCoupons.length === 0 && (
              <Alert className="border-2">
                <IconAlertCircle className="h-4 w-4" />
                <AlertTitle>No Coupons Found</AlertTitle>
                <AlertDescription>
                  {selectedType
                    ? 'No coupons found for this type. Try selecting a different type.'
                    : 'No coupons are currently available. Please check back later.'}
                </AlertDescription>
              </Alert>
            )}
          </TabsContent>

          <TabsContent value="clipped" className="mt-0">
            {!isAuthenticated && (
              <Alert className="mb-6 border-2">
                <IconAlertCircle className="h-4 w-4" />
                <AlertTitle>Sign In Required</AlertTitle>
                <AlertDescription>
                  Please sign in to view your clipped coupons.
                </AlertDescription>
              </Alert>
            )}

            {isAuthenticated && clippedCoupons.length === 0 && (
              <Alert className="border-2">
                <IconAlertCircle className="h-4 w-4" />
                <AlertTitle>No Clipped Coupons</AlertTitle>
                <AlertDescription>
                  You haven't clipped any coupons yet. Browse the "All Coupons" tab to find deals!
                </AlertDescription>
              </Alert>
            )}

            {isAuthenticated && clippedCoupons.length > 0 && (
              <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 md:gap-6">
                {clippedCoupons.map((coupon) => (
                  <CouponCard
                    key={coupon.id || coupon.offerId}
                    coupon={coupon}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Detail sheet for URL-opened coupons */}
        {selectedCoupon && (
          <CouponDetailSheet
            coupon={selectedCoupon}
            open={detailSheetOpen}
            onOpenChange={setDetailSheetOpen}
          />
        )}
      </div>
    </div>
  );
};

// Wrapper component that provides the ClipToastContext
const Coupons = () => {
  return (
    <ClipToastProvider>
      <CouponsContent />
      <ClipToastNotification />
    </ClipToastProvider>
  );
};

export default Coupons;
